// ---------------------------------------------------------------------------
// The shop's printers, as many as it owns.
//
// One resource addressed by id, because a role no longer identifies a device:
// a butcher may own a front counter, a grill bench and a meat bench, and all
// three answer to "preparation printer" or "receipt printer" depending only on
// what the owner has decided.
//
// Every mutation invalidates the CATEGORY caches too. A category's row shows
// the name of the bench it sends to, so renaming a printer has to redraw the
// Categories screen - otherwise the two disagree until somebody reloads, which
// is exactly the kind of thing that gets read as "the assignment was lost".
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { queryKeys } from '../lib/queryClient';

/// The two ways a printer is physically reached. A property of the CABLE, and
/// deliberately independent of what the printer is used for.
export type PrinterConnection = 'USB_WINDOWS' | 'NETWORK_TCP';

export interface PrinterDevice {
  id: string;
  /// What the shop calls it. The owner's handle on the device, and the only
  /// thing they should have to remember.
  name: string;
  connection: PrinterConnection | 'BROWSER_DIALOG' | 'LOCAL_CONNECTOR';
  /// The Windows print queue, on a USB printer.
  printerName: string | null;
  /// The LAN address, on a network printer.
  ipAddress: string | null;
  port: number;
  enabled: boolean;
  /// Exactly one living printer carries this.
  isDefaultReceipt: boolean;
  paperWidthMm: number;
  copies: number;
  /// How many living categories send preparation here. Shown on the card so
  /// the owner sees what a printer is doing before they try to retire it.
  assignedCategoryCount: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureNote: string | null;
  lastConnectionSuccessfulAt: string | null;
  lastConnectionError: string | null;
}

export interface PrinterDependencies {
  isDefaultReceipt: boolean;
  categories: Array<{ id: string; nameEn: string; nameAr: string }>;
  inUse: boolean;
}

export interface PrinterDevicePayload {
  name: string;
  connection: PrinterConnection;
  printerName?: string | null;
  ipAddress?: string | null;
  port?: number;
  paperWidthMm?: number;
  copies?: number;
}

const KEY = ['printer-devices'] as const;
const OUTPUTS_KEY = ['print-outputs'] as const;

export function usePrinterDevices() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<{ printers: PrinterDevice[] }>('/printer-devices'),
    select: (response) => response.printers,
    staleTime: 30_000,
  });
}

/// One routeable kind of paper, as the shop's central registry describes it.
///
/// The list comes from the server, never from a hardcoded array in a
/// component: what Yaghi can print is a fact about the system, and the screen
/// that routes it merely renders the answer. A GLOBAL output carries its
/// resolved route beside it; a CATEGORY output's routes live on the
/// categories themselves.
export interface PrintOutput {
  key: 'CUSTOMER_RECEIPT' | 'PREPARATION_TICKET';
  nameAr: string;
  nameEn: string;
  routingScope: 'GLOBAL' | 'CATEGORY';
  designer: string;
  printerId?: string | null;
  printerName?: string | null;
}

export function usePrintOutputs() {
  return useQuery({
    queryKey: OUTPUTS_KEY,
    queryFn: () => api.get<{ outputs: PrintOutput[] }>('/print-outputs'),
    select: (response) => response.outputs,
    staleTime: 30_000,
  });
}

/// Everything a printer change can be seen in.
function invalidatePrinters(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: KEY });
  // The routing section shows the resolved receipt route by name.
  void queryClient.invalidateQueries({ queryKey: OUTPUTS_KEY });
  // The Categories screen shows the bench each category prints to BY NAME.
  void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.categories });
}

export function useCreatePrinterDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PrinterDevicePayload) =>
      api.post<{ printer: PrinterDevice; message: string }>('/printer-devices', payload),
    onSuccess: () => invalidatePrinters(queryClient),
  });
}

export function useUpdatePrinterDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: PrinterDevicePayload & { id: string }) =>
      api.put<{ printer: PrinterDevice; message: string }>(`/printer-devices/${id}`, payload),
    onSuccess: () => invalidatePrinters(queryClient),
  });
}

export function useSetDefaultReceiptPrinter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.put<{ printer: PrinterDevice; message: string }>(
        `/printer-devices/${id}/default-receipt`,
        {},
      ),
    onSuccess: () => invalidatePrinters(queryClient),
  });
}

export function useSetPrinterEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.put<{ printer: PrinterDevice; message: string }>(`/printer-devices/${id}/enabled`, {
        enabled,
      }),
    onSuccess: () => invalidatePrinters(queryClient),
  });
}

/// What testing one printer found.
///
/// Two transports, one shape - and deliberately not one meaning. A network
/// printer that answers is CONNECTED; a USB queue Windows knows about is
/// AVAILABLE_TO_WINDOWS. Neither claims paper came out, because neither can.
export interface PrinterTestResult {
  printerId: string;
  name: string;
  connection: string;
  target: string | null;
  status:
    | 'CONNECTED'
    | 'AVAILABLE_TO_WINDOWS'
    | 'DISCONNECTED'
    | 'NOT_FOUND'
    | 'NOT_CONFIGURED'
    | 'DISABLED'
    | 'UNSUPPORTED_PLATFORM'
    /// This API is not the one that prints - the shop's print agent is, and it
    /// is the only thing with a route to the hardware. Nothing was dialled and
    /// nothing was claimed about the printer.
    | 'DISPATCHED_TO_AGENT';
  checkedAt: string;
  latencyMs: number | null;
  error?: string;
  /// Only on DISPATCHED_TO_AGENT: whether the shop's agent has been heard from.
  agentSeenRecently?: boolean;
  agentLastSeenAt?: string | null;
  windowsStatus?: string | null;
  offlineInWindows?: boolean;
}

/// Test ONE printer, by its own id.
///
/// The id is the whole point: with three benches, "test the kitchen printer"
/// has no single answer, and a role-keyed test would have reported the wrong
/// device's health while the owner watched.
export function useTestPrinterDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ result: PrinterTestResult }>(`/printer-devices/${id}/test`, {}),
    // The attempt is recorded on the printer, so the card's own last-checked
    // detail is refreshed by the same action.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useRetirePrinterDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/printer-devices/${id}`),
    onSuccess: () => invalidatePrinters(queryClient),
  });
}
